import type en from "../en/common";
import type { ArabicCatalog } from "../../catalog";

const ar: ArabicCatalog<typeof en> = {
  app: {
    ministry: "وزارة الاقتصاد والسياحة",
    ministryShort: "الوزارة",
    platform: "المنصة الوطنية للمنافسة",
    platformShort: "المنصة",
    department: "إدارة المنافسة",
    portalTitle: "بوابة الخدمات الخارجية",
    backofficeTitle: "إدارة الملفات",
    tagline: "منافسة عادلة، وتنفيذ شفاف، وثقة للمستثمرين.",
    demoBadge: "نموذج تجريبي للذكاء الاصطناعي الوكيلي",
    language: "اللغة",
    english: "English",
    arabic: "العربية",
    switchToArabic: "العربية",
    switchToEnglish: "English",
    skipToContent: "الانتقال إلى المحتوى الرئيسي",
    loading: "جار التحميل…",
    poweredBy: "مدعوم بالذكاء الاصطناعي الوكيلي مع تحقق بشري إلزامي",
  },
  actions: {
    add: "إضافة",
    signIn: "تسجيل الدخول",
    signInUaePass: "تسجيل الدخول بالهوية الرقمية",
    signOut: "تسجيل الخروج",
    continue: "متابعة",
    back: "رجوع",
    next: "التالي",
    submit: "إرسال",
    save: "حفظ",
    saveDraft: "حفظ كمسودة",
    cancel: "إلغاء",
    close: "إغلاق",
    approve: "اعتماد",
    accept: "قبول",
    reject: "رفض",
    edit: "تعديل",
    editAndApprove: "تعديل واعتماد",
    requestRevision: "طلب مراجعة",
    view: "عرض",
    viewAll: "عرض الكل",
    download: "تنزيل",
    upload: "رفع",
    search: "بحث",
    filter: "تصفية",
    refresh: "تحديث",
    retry: "إعادة المحاولة",
    send: "إرسال",
    copy: "نسخ",
    copied: "تم النسخ",
    saved: "تم الحفظ",
    start: "البدء",
    apply: "تطبيق",
    confirm: "تأكيد",
    delete: "حذف",
    escalate: "تصعيد إلى باحث قانوني",
    askAssistant: "سؤال المساعد",
    explain: "اشرح",
    openSource: "فتح المصدر",
    run: "تشغيل",
    rerun: "إعادة التشغيل",
    pay: "ادفع الآن",
    export: "تصدير",
    print: "طباعة",
    more: "المزيد",
    less: "أقل",
    showDetails: "إظهار التفاصيل",
    hideDetails: "إخفاء التفاصيل",
    complete: "إتمام المهمة",
  },
  nav: {
    mainMenu: "القائمة الرئيسية",
    home: "الرئيسية",
    dashboard: "لوحة المعلومات",
    services: "الخدمات",
    myCases: "طلباتي",
    publicRegister: "السجل العام",
    legalEnquiries: "الاستفسارات القانونية",
    thresholdTest: "التقييم الذاتي للحدود",
    notifications: "الإشعارات",
    organization: "المنشأة",
    help: "المساعدة",
    commandCenter: "الرئيسية",
    aiInbox: "صندوق الذكاء الاصطناعي",
    registers: "السجلات",
    cases: "الحالات",
    dataBank: "بنك البيانات",
    news: "الأخبار",
    marketStudies: "دراسات السوق",
    legalKnowledge: "المعرفة القانونية",
    advocacy: "السياسات",
    workflowStudio: "استوديو الإجراءات",
    integrations: "التكاملات",
    aiGovernance: "حوكمة الذكاء الاصطناعي",
    audit: "التدقيق والأمن",
    settings: "الإدارة",
    platformConsole: "إدارة المنصة",
    aiWorkspace: "المساعد الذكي",
    decisions: "قائمة القرارات",
    investigations: "التحقيقات",
    committee: "اللجنة",
    economicConcentration: "التركز الاقتصادي",
    investigationsComplaints: "التحقيقات والشكاوى",
    otherRequests: "طلبات أخرى",
    statistics: "الإحصاءات",
    resources: "المصادر",
    work: "الملفات",
    configured: "شاشات الإدارات",
    exemptions: "طلبات الإعفاء",
    grievances: "التظلمات",
    legalEnquiriesInternal: "الاستفسارات القانونية",
    outsideRequests: "طلبات البيانات والآراء الفنية",
  },
  caseTypes: {
    EconomicConcentration: "تركز اقتصادي",
    Complaint: "شكوى منافسة",
    Exemption: "طلب استثناء",
    ExemptionAmendment: "تعديل استثناء",
    Appeal: "تظلم / إعادة نظر",
    Whistleblowing: "بلاغ عن مخالفة",
    LegalEnquiry: "استفسار قانوني",
    MarketStudy: "دراسة سوق",
    MarketDataRequest: "تقديم المعززات للرد على معلومات واردة من الوزارة بشأن دراسة السوق",
    TechnicalOpinion: "تقديم الرأي الفني بشأن طلب وارد من الوزارة (G2G)",
    Generic: "معاملة",
  },
  caseTypeDescriptions: {
    EconomicConcentration:
      "الإخطار عن عملية اندماج أو استحواذ أو مشروع مشترك تتجاوز حد المبيعات {{turnover}} أو حصة سوقية {{share}}.",
    Complaint: "الإبلاغ عن ممارسات مخلة بالمنافسة مثل الاتفاقيات المقيدة أو تحديد أسعار إعادة البيع أو إساءة استغلال الوضع المهيمن.",
    Exemption: "طلب استثناء لاتفاقية مقيدة أو ممارسة ذات صلة بوضع مهيمن وفق المواد 5 إلى 8.",
    ExemptionAmendment: "تعديل نطاق أو شروط استثناء صادر سابقا.",
    Appeal: "طلب إعادة النظر في قرار خلال 15 يوما من الإخطار (المادة 34).",
    Whistleblowing: "الإبلاغ بسرية عن مخالفة محتملة لقانون تنظيم المنافسة.",
    LegalEnquiry: "الاستفسار عن المرسوم بقانون اتحادي رقم 36 لسنة 2023 ولائحته التنفيذية.",
    MarketStudy: "دراسة مستقلة لبنية قطاع أو سوق.",
    MarketDataRequest: "الرد على البيانات والمستندات المعززة التي تطلبها الوزارة من منشأتكم لدراسة سوق ({{marketDataBasis}}).",
    TechnicalOpinion: "للجهات المعنية والأجهزة التنظيمية القطاعية: تقديم الرأي الفني الذي تطلبه الوزارة بشأن شكوى أو طلب استثناء أو عملية تركز ({{opinionBasis}}).",
    Generic: "معاملة إدارية تدار وفق دليل إجراءات قابل للتهيئة.",
  },
  status: {
    Draft: "مسودة",
    Submitted: "مقدم",
    UnderReview: "قيد الدراسة",
    AwaitingApplicant: "بانتظار رد مقدم الطلب",
    Accepted: "مقبول",
    Rejected: "مرفوض",
    Published: "منشور",
    Investigation: "قيد التحقيق",
    Decision: "بانتظار القرار",
    Decided: "صدر القرار",
    Closed: "مغلق",
    Suspended: "معلق",
    Withdrawn: "مسحوب",
    Overdue: "متأخر",
    OnTrack: "ضمن المدة",
    AtRisk: "معرض للتأخير",
    Breached: "تجاوز المدة",
    Completed: "مكتمل",
    Pending: "قيد الانتظار",
    Paid: "مدفوع",
    Admissible: "مقبول شكلا",
    Inadmissible: "غير مقبول شكلا",
  },
  roles: {
    Applicant: "مقدم الطلب",
    Representative: "ممثل مفوض",
    EconomicResearcher: "باحث اقتصادي",
    LegalResearcher: "باحث قانوني",
    EconomicExpert: "خبير اقتصادي",
    LegalExpert: "خبير قانوني",
    HeadOfComplaints: "رئيس قسم الشكاوى",
    Director: "مدير إدارة المنافسة",
    AssistantUndersecretary: "الوكيل المساعد لقطاع الرقابة والحوكمة التجارية",
    Undersecretary: "وكيل الوزارة",
    Minister: "وزير الاقتصاد والسياحة",
    JudicialOfficer: "مأمور ضبط قضائي",
    CommitteeMember: "عضو لجنة تنظيم المنافسة",
    Admin: "مسؤول المنصة",
  },
  isic: "رمز النشاط {{code}}",
  ai: {
    badge: "ذكاء اصطناعي",
    pageRef: "صفحة {{n}}",
    assistant: "المساعد الذكي",
    proposal: "مقترح الذكاء الاصطناعي",
    proposals: "مقترحات الذكاء الاصطناعي",
    pendingReview: "بانتظار مراجعتك",
    confidence: "درجة الثقة",
    citations: "الاستشهادات",
    citation: "استشهاد",
    reasoning: "التحليل",
    explainability: "لماذا يقترح النظام ذلك",
    humanInLoop: "يتطلب تحققا بشريا قبل اتخاذ أي إجراء.",
    thinking: "جار التفكير…",
    retrieving: "جار استرجاع المصادر…",
    verifying: "جار التحقق من المراجع…",
    saveAsGolden: "حفظ كحالة ذهبية",
    revisionRequested: "طلبت المراجعة – يعيد المساعد صياغة المسودة",
    recordedAsGolden: "تم الحفظ في مجموعة تقييم التشغيلات المسجلة",
    toolCall: "استخدام أداة",
    drafting: "جار الصياغة…",
    verified: "تم التحقق بواسطة",
    approved: "معتمد",
    edited: "معدل ومعتمد",
    rejected: "مرفوض",
    proposed: "مقترح",
    agentActivity: "نشاط المساعد",
    modelUsed: "النموذج",
    tokens: "الرموز",
    guardrail: "ضابط أمان",
    piiRedacted: "تم إخفاء البيانات الشخصية قبل إرسالها للنموذج",
    piiMasked: "حجبت {{count}} من البيانات الشخصية",
    reviewedBy: "تمت المراجعة من",
    guardrails: "إشارات الحماية",
    disclaimer:
      "يقدم هذا المساعد معلومات وليس استشارة قانونية. كل إجابة تستشهد بالمادة المصدر. للحصول على توجيه ملزم، يرجى التصعيد إلى إدارة المنافسة.",
    data: {
      source: "المصدر", retrieved: "استرجعت في {{date}}", sourceData: "بيانات من المصادر المتصلة", sourceDataHint: "أرقام جلبها مساعد الذكاء الاصطناعي من الأنظمة المتصلة؛ وتودع مع المستند كمصنف عند الاعتماد.",
      table: "جدول", chart: "رسم بياني", rows: "الصفوف: {{count}}",
    },
    redlines: {
      title: "التعديلات المقترحة", count: "البنود: {{count}}", highCount: "مخاطر عالية: {{count}}",
      hint: "بندا بندا مقارنة بالمواقف المعيارية للوزارة: الصياغة الحالية مشطوبة، والصياغة المقترحة أسفلها.",
      clause: "البند {{n}}", current: "الصياغة الحالية", proposed: "الصياغة المقترحة", notQuoted: "لم تقتبس الصياغة الحالية – يرجى مطابقتها مع العقد.",
      rating: { high: "عالية", medium: "متوسطة", low: "منخفضة" },
      showAll: "عرض البنود كلها ({{count}})", showFewer: "عرض أقل",
    },
    figureCheck: {
      title: "تدقيق الأرقام", traced: "{{traced}} من {{total}} رقما موثقة المصدر", penalty: "خفضت درجة الثقة {{points}} نقطة",
      hint: "يطابق كل رقم في المسودة مع مستودع الإحصاءات ونتائج الأدوات والمصادر المستشهد بها، وتعرض المشكلات أولا.",
      status: { Verified: "مطابق", Rounded: "مقرب", Mismatch: "يخالف المصدر", Unsupported: "دون مصدر", Stale: "من إصدار سابق", PrecedenceBreach: "رقم دولي قدم رقما للدولة" },
      expected: "يعطي المصدر {{value}}", matchedTo: "مصدره", allTraced: "كل رقم في المسودة موثق بمصدره.",
      showAll: "عرض الأرقام كلها ({{count}})", showProblems: "عرض المشكلات فقط",
    },
    aiUse: {
      title: "سجل استخدام الذكاء الاصطناعي", prompt: "التعليمات", generated: "أعدت في", tools: "الأدوات المستخدمة", sources: "المصادر", more: "وغيرها ({{count}})", checks: "الفحوص",
      citations: "الاستشهادات {{value}}", quality: "الجودة {{value}}/100", review: "المراجعة البشرية",
      reviewText: "مسودة استشارية لا يسري أثرها إلا باعتماد مراجع مسمى. ويرافق هذا السجل المخرج أينما ذهب.",
      unsourced: "عبارات عن وقائع دون مصدر (عدت غير مدعومة)",
    },
    review: {
      draftSources: "المصادر التي تستند إليها المسودة",
      draftSourcesHint: "تفتح المصادر التي تقوم عليها المسودة قبل اعتمادها.",
      reviewNote: "ملاحظة المراجعة (اختياري)",
      reviewNoteRequired: "ملاحظة المراجعة",
      editHintChanged: "ستسجل تعديلاتك كتغذية راجعة لتقييم المساعد.",
      editHintNoChanges: "لا توجد تغييرات بعد.",
      reviseHint: "يبلغ مساعد الذكاء الاصطناعي بما يلزم تغييره، فترفض المسودة الحالية مع الملاحظة ويعيد المساعد الصياغة فورا؛ وتستخدم الملاحظة أيضا في التشغيلات اللاحقة.",
      reviseNoteHint: "يحسن التحديد: أي قسم، وأي مادة، وأي رقم. ويعرض المساعد الملاحظة في سجل نشاطه.",
      rejectNoteHint: "لماذا هذا المقترح غير مقبول؟ تغذى ملاحظات الرفض إلى مساعد الذكاء الاصطناعي في تشغيلاته التالية.",
      reasonsTitle: "أسبابك لقبول المسودة",
      reasonsLabel: "أسبابك",
      reasonsHint: "تؤثر هذه المسودة في نتيجة الملف. ويذكر المراجع بعباراته ما تحقق منه وما تستند إليه النتيجة، بما لا يقل عن {{count}} من الأحرف (كتبت {{typed}})؛ ولا يقبل نص منقول من المسودة.",
      aiDraft: "مسودة المساعد",
      yourVersion: "نسختك — ما سيعتمد",
      resetToDraft: "تجاهل تعديلاتك والبدء من جديد من مسودة المساعد",
      approveAndExecute: "اعتماد وتنفيذ",
      confirmTitle: "هل يعتمد المقترح وينفذ الإجراء؟",
      confirmBody: "يترتب على الاعتماد تنفيذ: {{action}}. ويصل ذلك إلى الأطراف أو السجل العام فور الاعتماد.",
      proposedAction: "الإجراء المقترح",
      manualRuns: "تشغيلات يدوية / خارج الخطوة",
    },
    redraft: {
      button: "إعادة الصياغة بالذكاء الاصطناعي",
      title: "إعادة الصياغة بالذكاء الاصطناعي",
      hint: "يذكر هنا ما يلزم تغييره، فيعيد المساعد كتابة النص ويعرضه بجانب النص الحالي، مع حفظ الوقائع والأرقام والاستشهادات، ولا يحفظ شيء قبل تطبيقه.",
      instruction: "ما الذي يجب تغييره؟",
      instructionPlaceholder: "مثلا: صياغة أقصر وأكثر رسمية، والإشارة إلى أن إيصال البنك لرسم التقديم ما زال ناقصا، وتقديم النواقص…",
      suggestions: {
        formal: "أكثر رسمية",
        shorter: "أقصر",
        clearer: "أوضح وأحسن تنظيما",
        grammar: "تصحيح النحو والأخطاء المطبعية",
        translateAr: "ترجمة إلى العربية",
        translateEn: "ترجمة إلى الإنجليزية",
        fromContext: "كتابته من المعيار والحكم المسجل وما وجدته المنصة",
      },
      run: "إعادة الصياغة",
      rerun: "حاول مجددا",
      apply: "تطبيق الصياغة",
      applied: "طبقت الصياغة، وتراجع قبل التسجيل أو الاعتماد كالمعتاد",
      current: "النص الحالي",
      proposed: "الصياغة المقترحة",
      waiting: "تظهر الصياغة هنا.",
      empty: "النص فارغ — سيكتبه المساعد من تعليماتك والسياق.",
      showChanges: "إظهار التغييرات",
      hideChanges: "إخفاء التغييرات",
      unchanged: "أعاد المساعد النص دون تغيير. ويحسن إعطاء تعليمات أكثر تحديدا.",
      offline: "إعادة الصياغة بالذكاء الاصطناعي غير متاحة ما دامت المنصة تعمل دون نموذج لغوي؛ ويحرر النص يدويا.",
      flags: "رصدت تعليمات مخفية داخل النص وتم تجاهلها؛ فتلزم قراءة النتيجة بعناية.",
      failed: "فشلت إعادة الصياغة",
      meter: "{{ms}} م.ث",
    },
    chat: {
      placeholder: "راسل {{assistant}}…",
      placeholderResearch: "السؤال المطلوب بحثه بعمق…",
      disclaimer: "يعد الذكاء الاصطناعي الإجابات من المصادر المعروضة وقد يخطئ، فيلزم التحقق من الاستشهادات. ولا يرسل شيء أو يحفظ أو يقرر دون تأكيد.",
      attach: "إرفاق مستند",
      attached: "مرفق",
      uploading: "جار الرفع…",
      uploadFailed: "تعذر إرفاق {{name}}",
      removeAttachment: "إزالة المرفق",
      readingAttachment: "جار رفع الملف وقراءته…",
      attachmentQueued: "بانتظار الرفع",
      attachmentLimit: "يمكن إرفاق 5 ملفات كحد أقصى لكل رسالة، بحجم لا يتجاوز 200 ميغابايت لكل ملف.",
      resolveAttachments: "انتظر اكتمال الرفع، ثم أعد محاولة رفع الملفات التي تعذر رفعها أو أزلها قبل الإرسال.",
      attachmentError: {
        interrupted: "انقطع رفع الملف. احتفظنا به هنا؛ أعد المحاولة عندما تكون جاهزا.",
        tooLarge: "يتجاوز حجم الملف 200 ميغابايت. يلزم ملف أصغر.",
        empty: "هذا الملف فارغ. يلزم ملف يحتوي على محتوى.",
        rejected: "تعذرت قراءة الملف. أزله واختر ملفا مدعوما وقابلا للقراءة.",
      },
      stop: "إيقاف",
      retry: "إعادة المحاولة",
      copy: "نسخ الإجابة",
      copyMarkdown: "نسخ بصيغة Markdown",
      sources: "المصادر",
      regenerate: "إعادة إنشاء الإجابة",
      copied: "تم النسخ",
      helpful: "مفيد",
      notHelpful: "غير مفيد",
      feedbackThanks: "شكرا — تستخدم ملاحظاتك في تدريب مجموعة التقييم.",
      toolsUsed: "استدعاءات الأدوات: {{count}}",
      showTrace: "عرض كيفية إنتاج هذه الإجابة",
      hideTrace: "إخفاء التتبع",
      stage: {
        thinking: "جار التفكير…",
        retrieving: "جار استرجاع المصادر…",
        tool: "جار استخدام الأدوات…",
        delegating: "جار التحويل إلى متخصص…",
        verifying: "جار التحقق من الاستشهادات…",
        writing: "جار الكتابة…",
      },
      delegatedTo: "أحيل إلى {{agent}}",
      delegatedAsync: "{{agent}} يعمل في الخلفية — ستظهر النتيجة هنا وفي صندوق الوارد.",
      specialistReply: "{{agent}} (متخصص)",
      runFailed: "فشل تشغيل المتخصص",
      proposalsPending: "بانتظار مراجعتك",
      artifact: "مخرج",
      artifactVersion: "الإصدار {{version}}",
      openArtifact: "فتح",
      confirmTitle: "يلزم التأكيد",
      confirm: "تأكيد",
      decline: "رفض",
      confirmExpires: "ينتهي {{when}}",
      confirmPendingHint: "هذا الإجراء يغير السجلات؛ ولا ينفذ إلا بعد تأكيدك.",
      confirmFailed: "تعذر تنفيذ الإجراء",
      confirmEdit: "تعديل",
      confirmDoneEditing: "إنهاء التعديل",
      confirmStatus: { Pending: "بانتظار التأكيد", Confirmed: "قيد التنفيذ…", Executed: "تم", Declined: "ألغي", Failed: "فشل", Expired: "انتهت الصلاحية" },
      confirmArg: {
        title: "العنوان", summary: "الملخص", note: "الملاحظة", category: "التصنيف", playbook: "دليل الإجراء", priority: "الأولوية", parameters: "التفاصيل", matter: "الملف",
        move: "الإجراء", from: "من", to: "إلى", task: "المهمة", answers: "الإجابات", then: "ثم ينتقل إلى", artifact: "المخرج", version: "الإصدار", format: "الصيغة",
        when: "عند", onlyIf: "بشرط", does: "ما يقوم به", reach: "لمن", nextRuns: "التشغيلات القادمة", reportsTo: "يبلغ في",
      },
      actionResult: "إجراء",
      openMatter: "فتح الملف",
      openWatcher: "فتح المراقب",
      openResult: "فتح",
      offline: "إجابة دون اتصال — تعمل المنصة دون نموذج لغوي.",
      guardrail: "حماية: {{flag}}",
      error: "لم يتمكن المساعد من إكمال هذه الدورة.",
      emptyTitle: "كيف يمكنني مساعدتك اليوم؟",
      emptyHint: "يمكن طرح سؤال، أو بحث موضوع بعمق، أو بدء إجراء. وتعرض كل إجابة مصادرها، ولا يتغير أي سجل دون تأكيدك.",
      tryAsking: "أمثلة على الأسئلة",
      mention: "مخاطبة متخصص",
      mentionHint: "تحال رسالتك إلى هذا المساعد المتخصص",
      playbook: "بدء دليل عمل",
      noMatches: "لا نتائج مطابقة",
      voice: "إدخال صوتي",
      listening: "جار الاستماع… والنقر يوقفه",
      voiceUnsupported: "الإدخال الصوتي غير مدعوم في هذا المتصفح",
      viewSource: "فتح المستند المصدر",
      viewCase: "فتح الطلب {{number}}",
      you: "أنت",
      newVersion: "إصدار جديد",
      startProcess: "بدء إجراء",
      startProcessHint: "فتح ملف دراسة أو سياسة يتبع خطوات الوزارة",
      askSpecialist: "سؤال متخصص",
      askSpecialistHint: "تذهب رسالتك التالية إلى هذا المتخصص",
      toSpecialist: "إلى {{agent}}",
      clearMode: "العودة إلى سؤال عادي",
      modes: {
        label: "ما الذي تريد القيام به؟",
        ask: "طرح سؤال",
        askHint: "إجابة سريعة مع مصادرها من الإحصاءات الرسمية وأوراق الوزارة.",
        research: "بحث معمق",
        researchHint: "يضع متخصص الأسئلة الفرعية ويقرأ الأدلة ثم يعود بموجز موثق بالمصادر. ويستغرق ذلك بضع دقائق.",
        process: "بدء إجراء",
        processHint: "فتح ملف دراسة أو سياسة يرشدك عبر خطوات الوزارة من السؤال حتى المخرج المعتمد.",
      },
      processBadge: "إجراء",
      noProcesses: "لا تتوفر إجراءات هنا.",
      saveDraft: "حفظ كمسودة",
      savedDraft: "حفظت في المسودات والملفات",
      saveFailed: "تعذر حفظ الإجابة",
      openDraft: "فتح",
      details: "التفاصيل",
      detailsModel: "النموذج",
      detailsLatency: "زمن الاستجابة",
      detailsTokens: "الرموز الداخلة / الخارجة",
      detailsTools: "استدعاءات الأدوات",
      askedSpecialist: "سأل {{agent}}",
      workedSteps_zero: "لا خطوات منجزة",
      workedSteps_one: "خطوة واحدة منجزة",
      workedSteps_two: "خطوتان منجزتان",
      workedSteps_few: "{{count}} خطوات منجزة",
      workedSteps_many: "{{count}} خطوة منجزة",
      workedSteps_other: "{{count}} خطوة منجزة",
      usedSources_zero: "لا مصادر مستخدمة",
      usedSources_one: "مصدر واحد مستخدم",
      usedSources_two: "مصدران مستخدمان",
      usedSources_few: "{{count}} مصادر مستخدمة",
      usedSources_many: "{{count}} مصدرا مستخدما",
      usedSources_other: "{{count}} مصدر مستخدم",
      artifactKind: { Markdown: "مستند", Table: "جدول", Chart: "رسم بياني", Json: "بيانات", Docx: "وورد", Pdf: "PDF", Xlsx: "إكسل" },
    },
    tools: {
      agent_brief: "قراءة إحاطة اليوم",
      agent_figures: "قراءة الأرقام من المستودع الإحصائي",
      agent_documents: "بحث في بنك المعرفة",
      agent_desks: "قراءة حالة المكاتب",
      agent_decisions: "قراءة ما ينتظرك",
      agent_country: "قراءة ملف الدول 360",
      agent_draft: "كتابة مسودة",
      agent_revise_draft: "تعديل المسودة",
      agent_suggest: "إضافة روابط وأسئلة للمتابعة",
      agent_plan: "تنفيذ الخطة",
      agent_team: "استشارة الفريق",
      agent_scenarios: "تشغيل السيناريوهات",
      agent_impact: "قراءة عدسة الأثر",
      agent_study: "كتابة الدراسة",
      search_knowledge: "بحث في مصادر الوزارة",
      search_regulations: "بحث في التشريعات",
      search_case_documents: "بحث في مستندات الملف",
      search_precedents: "بحث عن سوابق",
      find_indicator: "تحديد المؤشرات",
      get_economic_series: "قرأ الإحصاءات الرسمية",
      compare_economies: "قارن الدولة باقتصادات أخرى",
      economic_calc: "أجرى الحسابات",
      source_guide: "تحديد المصدر المناسب",
      target_gap: "قاس الفجوة عن المستهدف الوطني",
      nowcast_non_oil_gdp: "شغل التقدير الآني التجريبي للناتج غير النفطي",
      recent_publications: "راجع أحدث الإصدارات",
      scenario_run: "شغل السيناريوهات",
      appraise_options: "قيم البدائل",
      check_figures: "طابق الأرقام مع مستودع الإحصاءات",
      matter_assurance: "اطلع على فحوصات الضمان في الملف",
      matter_evidence: "اطلع على أدلة الملف",
      matter_kpis: "اطلع على مؤشرات الملف",
      matter_worksheet: "اطلع على ورقة عمل الملف",
      current_date: "تحقق من تاريخ اليوم",
      workspace_overview: "اطلع على مساحة العمل",
      list_matters: "استعرض ملفاتك",
      get_matter: "فتح الملف",
      create_matter: "أعد ملفا جديدا",
      fire_transition: "أعد الخطوة التالية في الملف",
      complete_task: "أنجز مهمة",
      add_matter_note: "أضاف ملاحظة إلى الملف",
      list_playbooks: "استعرض الإجراءات",
      list_agents: "استعرض المتخصصين",
      delegate_to_agent: "أحال إلى متخصص",
      draft_document: "صاغ مستندا",
      save_artifact: "حفظ مسودة",
      read_artifact: "قرأ مسودة",
      list_artifacts: "استعرض المسودات والملفات",
      file_artifact: "أودع مستندا",
      read_attachment: "قرأ المرفق",
      list_attachments: "استعرض المرفقات",
      list_watchers: "استعرض تنبيهاتك",
      propose_watcher: "أعد تنبيها",
      list_workflows: "استعرض مسارات العمل",
      propose_workflow_change: "اقترح تعديلا على مسار العمل",
      request_information: "أعد طلب معلومات",
      find_case: "عثر على الطلب",
      list_my_cases: "استعرض طلباتك",
      get_case_summary: "قرأ ملخص الطلب",
      get_market_shares: "قرأ الحصص السوقية",
      get_market_indicators: "قرأ مؤشرات السوق",
      compute_concentration: "قاس التركز السوقي",
      lookup_establishment: "بحث عن المنشأة",
      search_deal_watch: "بحث في أخبار الصفقات",
      lookup_deal: "بحث عن الصفقة",
      lookup_mergermarket_company: "بحث عن الشركة في بيانات الصفقات",
      get_case_deal_intelligence: "قرأ معلومات الصفقة",
      case_market_economics: "حلل السوق",
      vertical_checklist: "فحص الآثار الرأسية",
      assess_efficiencies: "قيم الكفاءات",
      hypothetical_monopolist_test: "أجرى اختبار تحديد السوق",
      pricing_pressure: "قاس ضغط التسعير",
      simulate_merger: "حاكى أثر الاندماج",
      local_catchments: "تحديد نطاقات التأثير المحلية",
      screen_tenders: "فحص المناقصات",
      reconcile_shares: "وفق بين الحصص السوقية",
      ownership_graph: "رسم هيكل الملكية",
      assess_notification_thresholds: "تحقق من حدود الإخطار",
      calculate_fee: "احتسب الرسم",
      working_days: "احتسب أيام العمل",
    },
    toolsActive: {
      agent_brief: "جار قراءة إحاطة اليوم",
      agent_figures: "جار قراءة الأرقام من المستودع الإحصائي",
      agent_documents: "جار البحث في بنك المعرفة",
      agent_desks: "جار قراءة حالة المكاتب",
      agent_decisions: "جار قراءة ما ينتظرك",
      agent_country: "جار قراءة ملف الدول 360",
      agent_draft: "جار كتابة مسودة",
      agent_revise_draft: "جار تعديل المسودة",
      agent_suggest: "جار إضافة روابط وأسئلة للمتابعة",
      agent_plan: "جار تنفيذ الخطة",
      agent_team: "جار استشارة الفريق",
      agent_scenarios: "جار تشغيل السيناريوهات",
      agent_impact: "جار قراءة عدسة الأثر",
      agent_study: "جار كتابة الدراسة",
      search_knowledge: "جار البحث في مصادر الوزارة",
      search_regulations: "جار البحث في التشريعات",
      search_case_documents: "جار البحث في مستندات الملف",
      find_indicator: "جار تحديد المؤشرات",
      get_economic_series: "جار قراءة الإحصاءات الرسمية",
      compare_economies: "جار مقارنة الدولة باقتصادات أخرى",
      economic_calc: "جار إجراء الحسابات",
      target_gap: "جار قياس الفجوة عن المستهدف الوطني",
      recent_publications: "جار مراجعة أحدث الإصدارات",
      scenario_run: "جار تشغيل السيناريوهات",
      check_figures: "جار مطابقة الأرقام",
      delegate_to_agent: "جار الإحالة إلى متخصص",
      draft_document: "جار صياغة المستند",
    },
  },
  time: {
    days_zero: "{{count}} يوم",
    days_one: "يوم واحد",
    days_two: "يومان",
    days_few: "{{count}} أيام",
    days_many: "{{count}} يوما",
    days_other: "{{count}} يوم",
    workingDays_zero: "{{count}} يوم عمل",
    workingDays_one: "يوم عمل واحد",
    workingDays_two: "يوما عمل",
    workingDays_few: "{{count}} أيام عمل",
    workingDays_many: "{{count}} يوم عمل",
    workingDays_other: "{{count}} يوم عمل",
    daysLeft_zero: "ينتهي الأجل اليوم",
    daysLeft_one: "بقي يوم واحد",
    daysLeft_two: "بقي يومان",
    daysLeft_few: "بقيت {{count}} أيام",
    daysLeft_many: "بقي {{count}} يوما",
    daysLeft_other: "بقي {{count}} يوم",
    daysOverdue_zero: "مستحق اليوم",
    daysOverdue_one: "متأخر يوما واحدا",
    daysOverdue_two: "متأخر يومين",
    daysOverdue_few: "متأخر {{count}} أيام",
    daysOverdue_many: "متأخر {{count}} يوما",
    daysOverdue_other: "متأخر {{count}} يوم",
    dueOn: "الاستحقاق {{date}}",
    startedOn: "بدأ في {{date}}",
    justNow: "الآن",
    today: "اليوم",
    yesterday: "أمس",
    overdue: "متأخر",
  },
  conducts: {
    RestrictiveAgreement: "اتفاق مقيد للمنافسة", Cartel: "تثبيت الأسعار / كارتل", ResalePriceMaintenance: "فرض سعر إعادة البيع", BidRigging: "التواطؤ في العطاءات", MarketSharing: "تقاسم السوق",
    AbuseOfDominance: "إساءة استغلال وضع مسيطر", PredatoryPricing: "التسعير الافتراسي", EconomicDependency: "استغلال التبعية الاقتصادية", ExclusiveDealing: "التعامل الحصري", RefusalToDeal: "رفض التعامل", Tying: "الربط / التجميع",
    UnnotifiedConcentration: "تركز اقتصادي غير مخطر به", StandstillBreach: "تنفيذ التركز قبل صدور الموافقة", Other: "أخرى",
  },
  misc: {
    inEnglish: "{{label}} بالإنجليزية",
    inArabic: "{{label}} بالعربية",
    type: "النوع",
    laterPhase: "يسلم هذا الجزء في مرحلة لاحقة من البناء.",
    new: "جديد",
    active: "نشط",
    caseNumber: "رقم الملف",
    status: "الحالة",
    updated: "آخر تحديث",
    conditions: "الشروط",
    documents: "المستندات",
    decision: "القرار",
    state: "المرحلة",
    risk: "المخاطر",
    reference: "المرجع",
    createdAt: "تاريخ الإنشاء",
    updatedAt: "آخر تحديث",
    assignedTo: "مسند إلى",
    unassigned: "غير مسند",
    priority: "الأولوية",
    riskScore: "درجة المخاطر",
    sla: "المدة القانونية",
    noResults: "لا توجد بيانات بعد",
    error: "حدث خطأ ما",
    errorDetail: "لم يتم إكمال الطلب. يرجى المحاولة مرة أخرى.",
    aed: "درهم",
    percent: "٪",
    yes: "نعم",
    no: "لا",
    optional: "اختياري",
    required: "إلزامي",
    all: "الكل",
    total: "الإجمالي",
    of: "من",
    page: "صفحة",
    rowsPerPage: "صفوف لكل صفحة",
    parties: "الأطراف",
    details: "التفاصيل",
    submittedAt: "تاريخ التقديم",
    organization: "المنشأة",
    timeline: "الجدول الزمني",
    none: "لا يوجد",
    title: "العنوان",
    summary: "الملخص",
    read: "تمت القراءة",
    time: "الوقت",
    uploadedBy: "رفعه",
  },
  ec: {
    combinedTurnover: "إجمالي المبيعات السنوية في الدولة",
    combinedShare: "الحصة السوقية المجمعة",
    notifiable: "يستوجب الإخطار",
    transactionType: "نوع العملية",
    consideration: "قيمة الصفقة",
    filingFee: "رسوم الطلب",
    marketShares: "الحصص السوقية",
    relevantMarket: "السوق المعنية",
  },
  decisionOutcomes: {
    Approved: "الموافقة",
    ApprovedWithConditions: "الموافقة المشروطة",
    Rejected: "الرفض",
    Prohibited: "الحظر",
    NoInfringement: "عدم وجود مخالفة",
    Infringement: "ثبوت المخالفة",
    Exempted: "منح الإعفاء",
    ExemptedWithConditions: "منح الإعفاء بشروط",
    Withdrawn: "السحب",
    Closed: "الإغلاق",
    GrievanceAccepted: "قبول التظلم",
    GrievanceRejected: "رفض التظلم",
    ImplicitRejection: "رفض ضمني (انقضاء 30 يوما)",
    NotSubjectToNotification: "عدم الخضوع لوجوب الإخطار",
    PrivateWarning: "خطاب إنذار",
    AdministrativeFine: "اقتراح التصالح (المادة 33(2))",
    ReferredToProsecution: "طلب تحريك الدعوى الجزائية (المادة 33(1))",
  },
  enums: {
    proposalKind: {
      TriageMemo: "مذكرة فرز", CompletenessReport: "تقرير الاستيفاء", DeficiencyNotice: "إشعار نقص", JurisdictionReport: "تقرير الاختصاص", LetterDraft: "مسودة خطاب", MarketAnalysis: "تحليل السوق",
      InterestedPartyAnalysis: "تحليل مرئيات الأطراف ذات المصلحة", ObjectionAssessment: "تقييم الاعتراض", RecommendationReport: "تقرير التوصية", PreliminaryReport: "تقرير أولي", FinalReport: "تقرير نهائي",
      DecisionBrief: "مذكرة قرار", InvestigationPlan: "خطة التحقيق", EvidenceAnalysis: "تحليل الأدلة", MeetingMinutes: "محضر اجتماع", ComplianceAssessment: "تقييم الامتثال", CommitteeReport: "تقرير اللجنة",
      EcInvestigationReport: "تقرير دراسة التركز", AppealAssessment: "تقييم التظلم", OfficialAnswer: "رد رسمي", MarketSignals: "إشارات السوق", DataBankCuration: "تنقية بنك البيانات", MarketStudy: "دراسة سوق", WorkflowChange: "تغيير إجراء", SlaAlert: "تنبيه مواعيد", DocumentProcessing: "معالجة المستندات",
      CompetitionAssessment: "تقييم أثر تشريع على المنافسة", DataRequest: "طلب بيانات", DealWatch: "فرز الصفقات", InternationalDigest: "الموجز الاقتصادي الدولي",
      EvidenceMatrix: "مصفوفة الأدلة", PolicyDraft: "مسودة سياسة", HorizonScan: "استشراف المستجدات", GrievanceAdmissibility: "قبول التظلم شكلا", EconomicStudy: "دراسة اقتصادية", DataChangeReport: "تقرير تغير البيانات", LegalOpinion: "رأي قانوني", LitigationMemo: "مذكرة تقاض", Report: "تقرير", PolicyNote: "مذكرة سياسات", ResearchSynthesis: "خلاصة بحثية", PositionPaper: "ورقة موقف", ConceptNote: "مذكرة مفاهيمية", MinisterialResolution: "قرار وزاري", PublicationDraft: "مسودة النشر", GrievanceAnalysis: "تحليل التظلم", EvidenceReview: "مراجعة الأدلة", ProfessionAnalysis: "تحليل المهنة", LegislationReview: "مراجعة تشريع", MatterDocument: "مستند الملف", ContractReview: "مراجعة عقد",
    },
    guardrail: {
      PromptInjection: "حقن تعليمات", DecisionLanguage: "لغة قرار", PiiLeak: "تسريب بيانات شخصية", MissingCitations: "استشهادات مفقودة", UnknownCitation: "استشهاد غير معروف", NumberNotFromTool: "رقم ليس من أداة",
      LanguageMismatch: "عدم تطابق اللغة", UnsupportedClaim: "ادعاء غير مدعوم", SchemaViolation: "مخالفة البنية", OutputTruncated: "مخرجات مقتطعة", TokenBudgetExceeded: "تجاوز ميزانية الرموز", QualityAttention: "الجودة: يحتاج انتباها",
      ArabicParity: "النص العربي يختلف عن الإنجليزي", FigureCheck: "تدقيق الأرقام", ConfidenceLanguage: "دون درجة احتمال أو ثقة", ArticleNotInCorpus: "مادة غير موجودة في المدونة القانونية", CitationUnverified: "استشهاد لم يحكم فيه",
      ModelUnavailable: "الذكاء الاصطناعي غير متاح", ModelUnavailableBuilderFallback: "نفذ دون ذكاء اصطناعي", ModelUnavailableSuperAgentFallback: "أعد دون ذكاء اصطناعي", FabricationDeclined: "رفض طلب اختلاق بيانات", UndatedFigure: "رقم دون تاريخ",
      UnsourcedStatement: "عبارة دون مصدر",
      FreshnessNotChecked: "لم تجر مطابقته مع أحدث المصادر الرسمية",
    },
    documentCategory: {
      Decision: "قرار", TransactionAgreement: "اتفاقية الصفقة", FinancialStatements: "القوائم المالية", MarketReport: "تقرير السوق", OrgChart: "هيكل الملكية", PowerOfAttorney: "وكالة قانونية", OverlapAnalysis: "تحليل التداخلات", PublicSummary: "ملخص عام",
      Efficiencies: "الكفاءات", Agreement: "اتفاقية", ComplainantIdentity: "هوية المشتكي", ConductEvidence: "أدلة الممارسة", HarmEvidence: "أدلة الضرر", MarketPosition: "الوضع في السوق", MarketInformation: "معلومات السوق",
      PricingEvidence: "أدلة التسعير", EssentialGoodsEvidence: "أدلة السلع الأساسية", PartyResponse: "رد الطرف", Report: "تقرير", Other: "أخرى",
    },
    letterKind: {
      Acknowledgement: "إشعار استلام", DeficiencyNotice: "إشعار نقص", RequestForInformation: "طلب معلومات", Reminder: "تذكير", Warning: "تحذير", AcceptanceNotice: "إشعار قبول", RejectionNotice: "إشعار رفض",
      PublicationNotice: "إشعار نشر", InvitationToComment: "دعوة لإبداء المرئيات", ObjectionNotice: "إشعار اعتراض", MeetingInvitation: "دعوة اجتماع", ReportShare: "مشاركة تقرير", DecisionNotice: "إشعار قرار", ExtensionNotice: "إشعار تمديد", General: "عام",
    },
    channel: { PortalInbox: "البوابة الإلكترونية", Email: "البريد الإلكتروني", Sms: "رسالة نصية", OfficialLetter: "خطاب رسمي" },
    letterStatus: { Draft: "مسودة", PendingApproval: "بانتظار الاعتماد", Approved: "معتمد", Sent: "مرسل", Delivered: "تم التسليم", Failed: "تعذر الإرسال", Cancelled: "ملغى" },
    submitterType: { Competitor: "منافس", Customer: "عميل", Supplier: "مورد", Distributor: "موزع", Association: "جمعية تجارية", Regulator: "جهة تنظيمية قطاعية", Consumer: "مستهلك", Other: "جهة أخرى" },
    partyRole: {
      NotifyingParty: "الطرف المخطر", AcquiringParty: "الطرف المستحوذ", TargetParty: "الطرف المستهدف", JointVenturePartner: "شريك المشروع المشترك", Complainant: "المشتكي", Respondent: "المشكو في حقه", InterestedParty: "طرف ذو مصلحة", Objector: "المعترض",
      Applicant: "مقدم الطلب", Representative: "الممثل المفوض", Whistleblower: "المبلغ", Other: "أخرى",
    },
    classification: { Public: "عام", Internal: "داخلي", Confidential: "سري", Secret: "سري للغاية" },
    extractionStatus: { Pending: "بانتظار القراءة", Processing: "قيد القراءة", Completed: "مقروء", Failed: "تعذرت قراءته" },
    band: { Unconcentrated: "غير مركز", ModeratelyConcentrated: "مركز بدرجة متوسطة", HighlyConcentrated: "مركز بدرجة عالية" },
    readiness: { Complete: "مكتمل", Partial: "جزئي", Missing: "مفقود", Optional: "اختياري", Present: "متوفر", Deficient: "ناقص", NotRequired: "غير مطلوب" },
    signalKind: { Info: "معلومة", Warning: "تحذير", Error: "خطأ", Tip: "نصيحة", NewsMention: "ذكر إخباري", PriceParallelism: "توازي الأسعار", ResalePriceSignal: "إشارة سعر إعادة البيع", ShareJump: "قفزة في الحصة", TenderPattern: "نمط عطاءات" },
    signalStatus: { New: "جديد", Escalated: "تم التصعيد", Dismissed: "مستبعد" },
    evidenceDirection: { Supports: "يدعم", Contradicts: "يناقض", Neutral: "محايد" },
    stance: { Support: "تأييد", Concern: "قلق", Oppose: "معارضة", Neutral: "محايد" },
    objectionOutcome: { Open: "مفتوح", Upheld: "مقبول – يؤخذ في الاعتبار عند الدراسة", Dismissed: "مرفوض", Admissible: "مقبول شكلا", Inadmissible: "غير مقبول شكلا", Pending: "قيد الانتظار" },
    meetingKind: { Hearing: "جلسة استماع", FieldVisit: "زيارة ميدانية", Consultation: "تشاور" },
    transactionType: { Merger: "اندماج", Acquisition: "استحواذ على السيطرة", JointVenture: "مشروع مشترك كامل الوظائف", AssetTransfer: "نقل أصول", ControlChange: "تحول من سيطرة مشتركة إلى منفردة", Agreement: "اتفاق", MinorityStake: "حصة أقلية" },
    paymentStatus: { Pending: "بانتظار السداد", Paid: "مسدد", Failed: "تعذر السداد", Refunded: "مسترد", Waived: "معفى", Reversed: "عكس السداد", RefundPending: "الرد بانتظار التأكيد" },
    dataSourceKey: {
      registry: "السجل الاقتصادي الوطني", fcsc: "المركز الاتحادي للتنافسية والإحصاء", mof: "وزارة المالية", casefiling: "ملفات الطلبات", marketfeed: "تغذية معلومات السوق",
      cbuae: "مصرف الإمارات العربية المتحدة المركزي", cma: "هيئة سوق رأس المال", dfsa: "سلطة دبي للخدمات المالية", "ner-g2g": "السجل الاقتصادي الوطني – الربط الحكومي",
      "moet-ubo": "سجلات المستفيد الحقيقي والشركاء لدى الوزارة", "moet-agencies": "سجل الوكالات التجارية لدى الوزارة", "moet-ec-register": "سجل قرارات التركز الاقتصادي لدى الوزارة",
      fta: "الهيئة الاتحادية للضرائب", mohre: "وزارة الموارد البشرية والتوطين", moiat: "وزارة الصناعة والتكنولوجيا المتقدمة", "icp-customs": "الهيئة الاتحادية للهوية والجنسية والجمارك وأمن المنافذ",
      "mof-procurement": "منصة المشتريات الرقمية لوزارة المالية", "tdra-telecom": "هيئة تنظيم الاتصالات والحكومة الرقمية", ubo: "بيانات المستفيد الحقيقي",
      fsra: "سلطة تنظيم الخدمات المالية في سوق أبوظبي العالمي", gcaa: "الهيئة العامة للطيران المدني", moei: "وزارة الطاقة والبنية التحتية", "mohap-ede": "وزارة الصحة ووقاية المجتمع ومؤسسة الإمارات للدواء",
      "dha-doh": "هيئة الصحة بدبي ودائرة الصحة في أبوظبي", "khda-adek": "هيئة المعرفة والتنمية البشرية ودائرة التعليم والمعرفة", "dld-adrec": "دائرة الأراضي والأملاك في دبي ومركز أبوظبي العقاري",
      "dubai-det": "دائرة الاقتصاد والسياحة في دبي", "abudhabi-adra": "دائرة التنمية الاقتصادية في أبوظبي", "sharjah-sedd": "دائرة التنمية الاقتصادية في الشارقة",
      "northern-deds": "دوائر التنمية الاقتصادية في عجمان ورأس الخيمة والفجيرة وأم القيوين", bayanat: "المنصة الوطنية للبيانات المفتوحة", wam: "وكالة أنباء الإمارات",
      aecb: "شركة الاتحاد للمعلومات الائتمانية", chambers: "غرف التجارة في دبي وأبوظبي", mergermarket: "ميرجرماركت",
    },
    agreementType: { Horizontal: "اتفاق أفقي", Vertical: "اتفاق رأسي", JointPurchasing: "شراء مشترك", "R&D": "بحث وتطوير", Standardisation: "توحيد المعايير", Other: "أخرى" },
    overlapType: { Horizontal: "أفقي", Vertical: "رأسي", Conglomerate: "تكتلي", None: "لا يوجد" },
    proposalStatus: { Proposed: "مقترح", Approved: "معتمد", Edited: "معدل ومعتمد", Rejected: "مرفوض", Superseded: "مستبدل", Expired: "منتهي" },
    runStatus: { Queued: "في الانتظار", Running: "قيد التنفيذ", Succeeded: "اكتمل", Failed: "لم يكتمل", Cancelled: "ألغي" },
    stepKind: { Started: "بدأ", Thought: "تفكير", Retrieval: "استرجاع", ToolCall: "استدعاء أداة", ToolResult: "نتيجة أداة", Draft: "مسودة", Guardrail: "ضابط حماية", Token: "رمز", Completed: "اكتمل", Failed: "فشل" },
    integrationHealth: { Healthy: "سليم", Degraded: "متدهور", Down: "متوقف", Unknown: "غير معروف" },
    dataSource: { CaseFiling: "ملف الطلب", FCSC: "إحصاءات المركز الاتحادي", Registry: "السجل الاقتصادي الوطني", MOF: "وزارة المالية", MarketStudy: "دراسة سوق", Manual: "إدخال يدوي" },
    corpusKind: { Law: "قانون", CabinetDecision: "قرار مجلس الوزراء", MinisterialDecision: "قرار وزاري", Guideline: "دليل إرشادي", Procedure: "إجراء", Precedent: "سابقة", ComparativeReference: "مرجع مقارن", Faq: "أسئلة شائعة", Report: "تقرير", Policy: "سياسة", InternationalGuidance: "إرشادات دولية", Agreement: "اتفاقية", Strategy: "استراتيجية", Statistics: "إحصاءات", Methodology: "منهجية", Study: "دراسة", Opinion: "رأي قانوني", Briefing: "إحاطة", Paper: "ورقة", Framework: "إطار", Minutes: "محضر اجتماع", Feedback: "ملاحظات", Presentation: "عرض تقديمي" },
    triggerSource: { Workflow: "سير العمل", Manual: "يدوي", Revision: "مراجعة", Schedule: "مجدول", Chat: "محادثة", Delegation: "تفويض" },
    priority: { Low: "منخفضة", Medium: "متوسطة", High: "عالية", Critical: "حرجة" },
    slaHealth: { OnTrack: "ضمن المدة", AtRisk: "معرض للخطر", Breached: "متجاوز", Completed: "مكتمل", Paused: "متوقف مؤقتا" },
    workflowState: {
      RemedyDesign: "تصميم التدابير العلاجية واختبار السوق", UndersecretaryDecision: "قرار وكيل الوزارة في وجوب الإخطار", PenaltyRecommendation: "التوصية بالإجراء",
      Draft: "مسودة", PreNotification: "مناقشة ما قبل الإخطار", Received: "تم الاستلام", FormalReview: "المراجعة الأولية", AwaitingCompletion: "بانتظار الاستيفاء", Registered: "مسجل", SimplifiedReview: "الدراسة المبسطة", SubstantiveReview: "الدراسة الموضوعية", PartyMeetings: "اجتماعات الأطراف", PreliminaryFindings: "النتائج الأولية – رد الأطراف",
      Publication: "منشور لإبداء المرئيات", ObjectionsReview: "دراسة الاعتراضات", Assessment: "تقرير التقييم", CommitteeOpinion: "معروض على لجنة تنظيم المنافسة", Escalation: "مرفوع إلى الوزير", MinisterDecision: "قرار الوزير", Decided: "صدر القرار", Closed: "مغلق", Withdrawn: "مسحوب",
      FormalCheck: "الفحص الشكلي", AwaitingComplainant: "بانتظار المشتكي", RejectOrSuspend: "رفض أو تعليق", PartiesNotification: "تم إخطار الأطراف", Investigation: "التحقيق", PreliminaryReportShared: "تمت مشاركة التقرير الأولي", FinalReport: "التقرير النهائي",
      Rejected: "مرفوض", Suspended: "معلق", MinisterReview: "مراجعة الوزير", Accepted: "مقبول", AwaitingApplicant: "بانتظار مقدم الطلب", ComplianceReview: "مراجعة التوافق", CommitteeReview: "مراجعة اللجنة", AmendmentReview: "دراسة التعديل",
      InformationGathering: "جمع المعلومات", ReportPreparation: "إعداد التقرير", Dismissed: "مستبعد", Open: "مفتوح", Escalated: "تم التصعيد", Answered: "تم الرد",
      Screening: "قيد الفحص", Returned: "معاد – غير واجب الإخطار",
      Commissioned: "تم التكليف", Scoping: "تحديد النطاق", DataCollection: "جمع البيانات", Analysis: "قيد التحليل", Consultation: "التشاور مفتوح", Drafting: "صياغة التقرير", Review: "قيد الاعتماد", Published: "منشور", Monitoring: "قيد المتابعة",
    },
    commitmentKind: { Structural: "هيكلي", Behavioural: "سلوكي", Reporting: "إبلاغي", Performance: "أدائي" },
    commitmentStatus: { Pending: "قيد الانتظار", InProgress: "قيد التنفيذ", Met: "مستوفى", Breached: "مخالف", Waived: "متنازل عنه" },
    decisionOutcome: {
      Approved: "موافقة", ApprovedWithConditions: "موافقة بشروط", Rejected: "رفض", Prohibited: "حظر", NoInfringement: "لا مخالفة", Infringement: "ثبوت المخالفة",
      Exempted: "استثناء", ExemptedWithConditions: "استثناء بشروط", Withdrawn: "سحب", Closed: "إغلاق", GrievanceAccepted: "قبول التظلم", GrievanceRejected: "رفض التظلم", ImplicitRejection: "رفض ضمني", NotSubjectToNotification: "غير خاضع لوجوب الإخطار",
    },
    sector: {
      "Retail – grocery": "التجزئة – البقالة", "Manufacturing – cement and building materials": "الصناعة – الأسمنت ومواد البناء", "Pharmaceuticals and healthcare": "الأدوية والرعاية الصحية", Telecommunications: "الاتصالات",
      "Transport and logistics": "النقل والخدمات اللوجستية", "Construction and contracting": "البناء والمقاولات", "Food and beverages": "الأغذية والمشروبات", "Education services": "خدمات التعليم", "Financial services": "الخدمات المالية", Other: "أخرى",
    },
  },
  ui: {
    close: "إغلاق", you: "أنت", showDraft: "عرض المسودة والاستشهادات", hideDraft: "إخفاء المسودة", noResults: "لا توجد نتائج", previousPage: "الصفحة السابقة", nextPage: "الصفحة التالية", switchLanguage: "تبديل اللغة", toggleNavigation: "تبديل التنقل",
    moderatelyConcentrated: "مركز بدرجة متوسطة", highlyConcentrated: "مركز بدرجة عالية", cardNumber: "رقم البطاقة", quality: "الجودة",
  },
  sso: {
    heading: "تسجيل الدخول بحساب الجهة",
    continueWith: "المتابعة باستخدام {{name}}",
    redirecting: "جار التحويل إلى {{name}}…",
    completing: "جار إكمال تسجيل الدخول…",
    startFailed: "تعذر بدء تسجيل الدخول.",
    protectedNote: "تسجل حسابات موظفي الوزارة الدخول بكلمة المرور الخاصة بكل حساب، ويصدر المسؤول هذه الحسابات.",
    errors: {
      noRole: "تم تسجيل الدخول، لكن لا تمنح أي من مجموعات الدليل المرتبطة بالحساب دورا في المنصة. يرجى طلب مطابقة المجموعة أو منح دور من المسؤول.",
      notProvisioned: "لا يقابل حساب الدليل حسابا في المنصة بعد. يرجى طلب الإضافة من المسؤول.",
      deactivated: "الحساب في المنصة معطل. يرجى التواصل مع المسؤول.",
      emailDomainNotAllowed: "لا يمكن للحسابات من نطاق بريدك الإلكتروني تسجيل الدخول هنا.",
      linkConflict: "يلزم أن يسمح المسؤول بربط هذا الحساب بحساب الوزارة.",
      mfa_not_performed: "لم يتضمن تسجيل الدخول بحساب الوزارة خطوة المصادقة الثنائية. يلزم تسجيل الدخول مرة أخرى.",
      mfa_reauthenticate: "تسجيل الدخول بحساب الوزارة أقدم مما تقبله المنصة. يلزم تسجيل الدخول مرة أخرى.",
      state_invalid: "انتهت صلاحية عملية تسجيل الدخول هذه أو استخدمت من قبل. يرجى البدء من جديد.",
      access_denied: "ألغي تسجيل الدخول.",
      provider_unavailable: "تسجيل الدخول بحساب الوزارة غير متاح حاليا.",
      generic: "لم يكتمل تسجيل الدخول.",
    },
  },
  auth: {
    title: "تسجيل الدخول",
    login: "البريد الإلكتروني للعمل أو اسم المستخدم",
    password: "كلمة المرور",
    passwordPlaceholder: "كلمة المرور",
    continue: "متابعة",
    showPassword: "إظهار كلمة المرور",
    hidePassword: "إخفاء كلمة المرور",
    capsLockOn: "مفتاح الأحرف الكبيرة مفعل",
    capsLockOff: "مفتاح الأحرف الكبيرة غير مفعل",
    forgot: "نسيت كلمة المرور؟",
    forgotHint: "يعيد المسؤول تعيينها",
    ministry: "تسجيل الدخول بحساب الوزارة",
    passwordInstead: "تسجيل الدخول بكلمة المرور بدلاً من ذلك",
    hidePasswordSignIn: "إخفاء تسجيل الدخول بكلمة المرور",
    loadingSignInOptions: "جار تحميل خيارات تسجيل الدخول…",
    or: "أو",
    noSelfSignup: "لا تسجيل ذاتي ولا روابط مشتركة. يصدر الحسابات مسؤول التحول الرقمي، وتؤدي 5 محاولات فاشلة إلى قفل الحساب مدة 15 دقيقة.",
    signedIn: "تم تسجيل الدخول",
    code: {
      title: "رمز التحقق",
      label: "الرمز المكون من 6 أرقام من تطبيق المصادقة",
      every: "رمز جديد كل 30 ثانية",
      verify: "التحقق وتسجيل الدخول",
      verified: "تم التحقق من كلمة المرور",
      notYou: "لست أنت؟",
      lost: "فقدت جهازك؟",
      lostHint: "يعيد مكتب خدمة التحول الرقمي إعداد المصادقة بعد التحقق من الهوية",
    },
    change: {
      title: "تعيين كلمة مرور جديدة",
      hint: "أصدر المسؤول كلمة مرور مؤقتة، ويلزم اختيار كلمة مرور خاصة للمتابعة.",
      new: "كلمة المرور الجديدة",
      confirm: "تأكيد كلمة المرور الجديدة",
      rule: "14 حرفا على الأقل. عبارة مرور من عدة كلمات أسهل في التذكر.",
      submit: "الحفظ والمتابعة",
    },
    rules: {
      title: "قواعد كلمة المرور",
      length: "14 حرفا على الأقل",
      notReused: "ليست من كلمات المرور الخمس الأخيرة",
      notBreached: "ليست ضمن قوائم كلمات المرور المسربة",
      noName: "لا تتضمن الاسم أو البريد الإلكتروني",
      match: "الإدخالان متطابقان",
      server: "يتحقق الخادم منها عند المتابعة",
    },
    enrol: {
      title: "إعداد تطبيق المصادقة",
      scan: "المسح بتطبيق المصادقة",
      hint: "Microsoft Authenticator أو Google Authenticator أو أي تطبيق TOTP. يتغير الرمز كل 30 ثانية.",
      qrAlt: "رمز QR لتطبيق المصادقة",
      manual: "يتعذر المسح؟ عرض مفتاح الإعداد",
      hideKey: "إخفاء مفتاح الإعداد",
      key: "مفتاح الإعداد",
      account: "الحساب",
      copy: "نسخ",
      copied: "تم النسخ",
      confirm: "الرمز المكون من 6 أرقام للتأكيد",
      submit: "التأكيد والإنهاء",
      preparing: "جار إعداد رمز المصادقة…",
      private: "لا يطلع المسؤول على كلمة المرور ولا على سر المصادقة، ويسجل الإعداد في سجل التدقيق.",
    },
    err: {
      invalid_credentials: "البريد الإلكتروني أو كلمة المرور غير صحيحة، أو أن الحساب مقفل مؤقتا.",
      attemptsLeft: "المحاولات المتبقية قبل القفل مدة 15 دقيقة: {{count}}",
      rate_limited: "محاولات كثيرة. يمكن إعادة المحاولة بعد بضع دقائق.",
      mfa_invalid: "الرمز غير صحيح. يلزم التحقق من ضبط الوقت في الهاتف واستخدام أحدث رمز.",
      codeAttemptsLeft: "المحاولات المتبقية للرمز في هذا الدخول: {{count}}",
      mfa_not_enrolled: "لا يوجد تطبيق مصادقة معد لهذا الحساب، ويمكن للمسؤول إعادة تعيينه.",
      mfa_code_reused: "سبق استخدام هذا الرمز. يظهر رمز جديد خلال ثوان.",
      challenge_expired: "استغرق تسجيل الدخول وقتا طويلا. يلزم البدء من جديد.",
      password_policy: "لا تستوفي كلمة المرور الجديدة قواعد كلمة المرور.",
      password_too_short: "كلمة المرور قصيرة جدا (14 حرفا على الأقل).",
      password_too_long: "كلمة المرور طويلة جدا (128 حرفا كحد أقصى).",
      password_invalid_characters: "تحتوي كلمة المرور على أحرف غير مسموح بها.",
      password_common: "كلمة المرور هذه شائعة أو سبق تسريبها، ويلزم اختيار غيرها.",
      password_contains_identity: "يجب ألا تتضمن كلمة المرور الاسم أو البريد الإلكتروني.",
      password_reused: "لا يمكن إعادة استخدام إحدى كلمات المرور الخمس الأخيرة.",
      password_mismatch: "كلمتا المرور غير متطابقتين.",
      mfa_not_performed: "لم يتضمن تسجيل الدخول بحساب الوزارة خطوة المصادقة الثنائية. يلزم تسجيل الدخول مرة أخرى.",
      mfa_reauthenticate: "تسجيل الدخول بحساب الوزارة أقدم مما تقبله المنصة. يلزم تسجيل الدخول مرة أخرى.",
      code_incomplete: "يلزم إدخال أرقام الرمز الستة كاملة.",
      network: "تعذر الوصول إلى الخادم. يمكن إعادة المحاولة بعد التحقق من الاتصال.",
      unavailable: "تسجيل الدخول بكلمة المرور غير متاح على هذا الخادم بعد.",
      generic: "لم يكتمل تسجيل الدخول. يمكن إعادة المحاولة.",
    },
    lastSignIn: "آخر تسجيل دخول: {{when}} من {{where}}",
    notice: {
      password_reset_by_admin: "أعاد {{name}} تعيين كلمة المرور الخاصة بك في {{date}}.",
      mfa_reset_by_admin: "أعاد {{name}} تعيين المصادقة الخاصة بك في {{date}}.",
      dismiss: "إغلاق",
    },
    session: {
      idleTitle: "هل ما زلت هنا؟",
      idleWarning: "حرصا على الأمان، ينتهي تسجيل الدخول خلال {{time}} بسبب عدم النشاط.",
      absoluteTitle: "الجلسة على وشك الانتهاء",
      absoluteWarning: "تنتهي جلستك عند {{time}}. يلزم حفظ العمل ثم تسجيل الدخول من جديد.",
      stay: "البقاء متصلا",
      signOut: "تسجيل الخروج",
      signOutNow: "تسجيل الخروج الآن",
    },
    reason: {
      idle: "تم تسجيل الخروج بعد 30 دقيقة من عدم النشاط.",
      expired: "بلغت الجلسة حدها الأقصى البالغ 10 ساعات. يلزم تسجيل الدخول مرة أخرى.",
      revoked: "أنهى المسؤول الجلسة أو أنهيت من جهاز آخر.",
      ended: "انتهت الجلسة. يلزم تسجيل الدخول مرة أخرى.",
      other_tab: "تم تسجيل الخروج في علامة تبويب أخرى.",
    },
    sessions: {
      title: "الأجهزة المسجل دخولها",
      current: "هذا الجهاز",
      revoke: "تسجيل خروج هذا الجهاز",
    },
    portal: {
      subtitle: "يسجل المتعاملون وممثلوهم الدخول بحساب UAE Pass المرتبط بالهوية الإماراتية.",
      uaePassOff: "تسجيل الدخول عبر UAE Pass غير متصل في هذه البيئة.",
      uaePassOffHint: "يسجل المتعاملون الدخول عبر UAE Pass بعد أن تربطه الوزارة. وحتى ذلك الحين لا تتيح هذه الصفحة طريقة دخول أخرى.",
      emailCode: "تسجيل الدخول برمز يرسل إلى البريد الإلكتروني",
      emailCodeHint: "للمنشآت والمستشارين الذين لا يملكون هوية UAE Pass، وتفعله الوزارة لكل بيئة.",
    },
  },
};

export default ar;
